<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import whatsappCallAPI from 'dashboard/api/whatsappCall';

// ============================================================================
// v109 (WhatsApp Call — Fase 4): DASHBOARD DE LIGAÇÕES — cards do período +
// tabela (direção, número, contato, agente, caixa, data/hora, duração,
// gravação). Dados da API v108 (admin-only por enquanto).
// ============================================================================

const route = useRoute();
const accountId = computed(() => route.params.accountId);

const PERIODOS = [
  { key: 'today', label: 'Hoje' },
  { key: 'yesterday', label: 'Ontem' },
  { key: '7d', label: '7 dias' },
  { key: '30d', label: '30 dias' },
  { key: 'month', label: 'Mês' },
];
const period = ref('today');
const direction = ref('');
const isLoading = ref(false);
const error = ref('');
const resumo = ref(null);
const chamadas = ref([]);

const fmtDur = s => {
  const t = Number(s) || 0;
  const m = String(Math.floor(t / 60)).padStart(2, '0');
  return `${m}:${String(t % 60).padStart(2, '0')}`;
};
const fmtData = iso =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const STATUS_LABEL = {
  ringing: 'Tocando',
  answered: 'Em atendimento',
  ended: 'Atendida',
  missed: 'Perdida',
  rejected: 'Recusada',
  failed: 'Falhou',
};
const STATUS_CLS = {
  ended: 'ok',
  answered: 'ok',
  missed: 'err',
  rejected: 'err',
  failed: 'err',
  ringing: 'warn',
};

const load = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    const params = { period: period.value };
    if (direction.value) params.direction = direction.value;
    const [{ data: r }, { data: lista }] = await Promise.all([
      whatsappCallAPI.resumo(accountId.value, params),
      whatsappCallAPI.lista(accountId.value, params),
    ]);
    resumo.value = r;
    chamadas.value = lista;
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    isLoading.value = false;
  }
};

const selecionar = key => {
  period.value = key;
  load();
};
const filtrarDirecao = d => {
  direction.value = direction.value === d ? '' : d;
  load();
};

onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="overflow-auto p-4 w-full h-full bg-n-background">
    <div class="flex flex-wrap gap-3 items-center mb-5">
      <span class="wc-ico"><span class="i-lucide-phone size-5" /></span>
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">WhatsApp Call · Dashboard</h1>
        <p class="text-xs text-n-slate-11">Ligações do número oficial — recebidas e realizadas</p>
      </div>
      <span class="flex-1" />
      <div class="flex gap-1.5">
        <button
          v-for="p in PERIODOS"
          :key="p.key"
          type="button"
          class="px-3 py-1.5 text-xs font-semibold rounded-lg border"
          :class="period === p.key ? 'bg-n-brand text-white border-transparent' : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'"
          @click="selecionar(p.key)"
        >
          {{ p.label }}
        </button>
      </div>
    </div>

    <p v-if="error" class="p-3 mb-4 text-sm rounded-lg border border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11">{{ error }}</p>

    <!-- cards -->
    <div v-if="resumo" class="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-3 lg:grid-cols-7">
      <div class="wc-card"><span>Total</span><b class="tabular-nums">{{ resumo.total }}</b></div>
      <button type="button" class="wc-card clicavel" :class="{ ativo: direction === 'inbound' }" @click="filtrarDirecao('inbound')">
        <span>📥 Recebidas</span><b class="tabular-nums">{{ resumo.recebidas }}</b>
      </button>
      <button type="button" class="wc-card clicavel" :class="{ ativo: direction === 'outbound' }" @click="filtrarDirecao('outbound')">
        <span>📤 Realizadas</span><b class="tabular-nums">{{ resumo.realizadas }}</b>
      </button>
      <div class="wc-card verde"><span>Atendidas</span><b class="tabular-nums">{{ resumo.atendidas }}</b></div>
      <div class="wc-card vermelho"><span>Perdidas</span><b class="tabular-nums">{{ resumo.perdidas }}</b></div>
      <div class="wc-card"><span>Tempo total</span><b class="tabular-nums">{{ fmtDur(resumo.tempo_total_s) }}</b></div>
      <div class="wc-card"><span>Tempo médio</span><b class="tabular-nums">{{ fmtDur(resumo.tempo_medio_s) }}</b></div>
    </div>

    <div v-if="isLoading" class="py-12 text-center text-n-slate-11">
      <span class="inline-block i-lucide-loader-circle animate-spin size-7" />
    </div>

    <p v-else-if="!chamadas.length" class="py-12 text-center text-sm text-n-slate-11">
      Nenhuma ligação no período.
    </p>

    <!-- tabela -->
    <div v-else class="overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-[11px] uppercase text-n-slate-11 bg-n-solid-1">
            <th class="px-3 py-2">Direção</th>
            <th class="px-3 py-2">Número</th>
            <th class="px-3 py-2">Contato</th>
            <th class="px-3 py-2">Agente</th>
            <th class="px-3 py-2">Caixa</th>
            <th class="px-3 py-2">Data/Hora</th>
            <th class="px-3 py-2">Duração</th>
            <th class="px-3 py-2">Status</th>
            <th class="px-3 py-2">Gravação</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in chamadas" :key="c.id" class="border-t border-n-weak text-n-slate-12">
            <td class="px-3 py-2">{{ c.direction === 'inbound' ? '📥 Recebida' : '📤 Realizada' }}</td>
            <td class="px-3 py-2 tabular-nums">{{ c.phone_number || '—' }}</td>
            <td class="px-3 py-2">{{ c.contato || '—' }}</td>
            <td class="px-3 py-2">{{ c.agente || '—' }}</td>
            <td class="px-3 py-2">{{ c.inbox_nome || '—' }}</td>
            <td class="px-3 py-2 tabular-nums">{{ fmtData(c.started_at) }}</td>
            <td class="px-3 py-2 tabular-nums">{{ fmtDur(c.duration_s) }}</td>
            <td class="px-3 py-2">
              <span class="wc-badge" :class="STATUS_CLS[c.status] || 'warn'">{{ STATUS_LABEL[c.status] || c.status }}</span>
            </td>
            <td class="px-3 py-2">
              <audio v-if="c.recording_url" :src="c.recording_url" controls preload="none" class="h-8 max-w-[220px]" />
              <span v-else class="text-xs text-n-slate-11">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.wc-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #25d366, #128c7e);
  color: #06301a;
  box-shadow: 0 4px 14px rgba(37, 211, 102, 0.3);
}
.wc-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  text-align: left;
}
.wc-card span { font-size: 11px; color: #9ca3af; }
.wc-card b { font-size: 20px; color: #fff; }
.wc-card.verde b { color: #25d366; }
.wc-card.vermelho b { color: #ff9592; }
.wc-card.clicavel { cursor: pointer; }
.wc-card.clicavel:hover { border-color: rgba(37, 211, 102, 0.4); }
.wc-card.ativo { border-color: #25d366; box-shadow: 0 0 0 2px rgba(37, 211, 102, 0.25); }
.wc-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
}
.wc-badge.ok { color: #25d366; background: rgba(37, 211, 102, 0.14); }
.wc-badge.err { color: #ff9592; background: rgba(229, 72, 77, 0.16); }
.wc-badge.warn { color: #fcd34d; background: rgba(245, 158, 11, 0.14); }
</style>
